import { Component, OnDestroy, OnInit } from '@angular/core';
import { CityModel, HxAuthService, HxCityService, HxStoreService, langMap, PromoBannerModel, PromoStory, SavePromoBannerRequest, UiLabel } from 'hx-services';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, from, Observable, of, Subject } from 'rxjs';
import { NzModalComponent, NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzUploadChangeParam, NzUploadComponent, NzUploadFile } from 'ng-zorro-antd/upload';
import { map } from 'rxjs/operators';
import { buildGooglePublicUrl, getBase64 } from '@adm-web-app/utils';
import { HttpClient } from '@angular/common/http';
import { PromoBannerService } from '@adm-web-app/services/promo-banner.service';
import { NotificationService } from '@adm-web-app/services/notification.service';
import { StoreEditModal } from '@adm-web-app/modules/promo-banners/modal/story-edit.modal';
import { CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { HxCitySelectComponent, HxLabelEditComponent } from 'hx-components';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { JsonPipe, NgFor, NgIf } from '@angular/common';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { MarkdownComponent } from 'ngx-markdown';
import { NzTagComponent } from 'ng-zorro-antd/tag';

interface PreviewFile {
  fileName?: string;
  link?: string;
  data?: string;
  image?: string;
  visible?: boolean;
}

@Component({
    selector: 'hc-promo-edit',
    templateUrl: './promo-banner-edit.component.html',
    styleUrls: ['./promo-banner-edit.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzSkeletonComponent, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, NzInputDirective, NzRadioGroupComponent, NzRadioComponent, NzDatePickerComponent, NzInputNumberComponent, HxLabelEditComponent, HxCitySelectComponent, NzDividerComponent, NzButtonComponent, NzWaveDirective, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzTbodyComponent, CdkDropList, NgFor, CdkDrag, NzButtonGroupComponent, NzIconDirective, NzPopconfirmDirective, NgIf, NzAlertComponent, NzUploadComponent, NzModalComponent, MarkdownComponent, NzTagComponent, JsonPipe, TranslocoPipe]
})
export class PromoBannerEditComponent implements OnInit, OnDestroy {

  model?: PromoBannerModel;
  isEdit = false;
  isCreate = false;
  form = new FormGroup({
    name: new FormControl<string | null>(null, [Validators.required]),
    description: new FormControl<UiLabel | null>(null, [Validators.required]),
    title: new FormControl<UiLabel | null>(null, [Validators.required]),
    mainBanner: new FormControl<boolean | null>(null, [Validators.required]),
    banner: new FormControl<boolean | null>(null, [Validators.required]),
    promo: new FormControl<boolean | null>(null, [Validators.required]),
    enabled: new FormControl<boolean | null>(null, [Validators.required]),
    startDate: new FormControl<string | null>(null, [Validators.required]),
    endDate: new FormControl<string | null>(null),
    cityIds: new FormControl<number[] | null>(null, [Validators.required]),
    sortOrder: new FormControl<number | null>(null, [Validators.required]),
  });
  isSaving = false;
  isLoading = {
    previewUpload: false,
    files: false,
    model: false,
  };
  stories: PromoStory[] = [];
  // files related vars
  fileList: NzUploadFile[] = [];
  filesUploadUrl?: string;
  preview: PreviewFile = {};
  requiredFiles: { fileName: string, exist: boolean }[] = [];

  private $destroyed = new Subject<void>();

  constructor(
    private aRoute: ActivatedRoute,
    private modal: NzModalService,
    private promoBannerService: PromoBannerService,
    private tr: TranslocoService,
    private notification: NzNotificationService,
    private storeService: HxStoreService,
    private cityService: HxCityService,
    private http: HttpClient,
    private router: Router,
    private auth: HxAuthService,
    private nt: NotificationService,
  ) {
  }

  fileUploadHeadersFn = () => {
    const token = this.auth.keycloakToken();
    return {
      Authorization: `Bearer ${token}`,
      'ngsw-bypass': 'true'
    };
  };

  handlePreviewFn = async (file: NzUploadFile) => {
    if (!file.url && !file['preview'] && file.originFileObj) {
      file['preview'] = await getBase64(file.originFileObj);
    }
    this.preview = {};
    this.preview.image = file.url ?? file['preview'];
    this.preview.visible = true;
    this.preview.fileName = file.filename;
    if (this.preview.fileName?.toLowerCase().endsWith('.md') && file.url) {
      this.preview.image = undefined;
      const response = await firstValueFrom(this.http.get(file.url, {responseType: 'text'}));
      if (typeof response === 'string') {
        this.preview.data = response;
      }
    }

    this.preview.link = 'https://storage.googleapis.com/' + buildGooglePublicUrl(`promo/${this.form.controls.name.value}/${file.name}`);
  };

  fileRemoveFn: (file: NzUploadFile) => Observable<boolean> = file => {
    if (!file.name) {
      return of(false);
    }
    const promoName = this.form.controls.name.value;
    if (promoName) {
      return of(this.promoBannerService.removeFile(promoName, file.name)).pipe(map(() => true));
    }
    return of(false);
  };

  async ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as PromoBannerModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.isCreate = true;
    }
    this.updateForm();

    if (this.isEdit) {
      this.filesUploadUrl = this.promoBannerService.getFileUploadUrl(this.model.name);
      this.form.controls.name.disable();
      this.isLoading.files = true;
      try {
        const [cities, files] = await Promise.all([
          this.cityService.getAllCities(),
          this.promoBannerService.getPromoImageUrls(this.model.name),
        ]);
        const promoName = this.form.controls.name.value;
        const cityIds = this.form.controls.cityIds.value ?? [];
        this.fileList = files.filter(file => file.size !== '0' && !file.name?.startsWith(`promo/${promoName}/stories/`))
          .map(file => ({
            uid: file.id,
            size: Number(file.size),
            name: file.name?.replace(`promo/${promoName}/`, ''),
            filename: file.name?.replace(`promo/${promoName}/`, ''),
            url: file.mediaLink,
            type: file.contentType,
            status: 'done',
          } as NzUploadFile));
        this.onCityChanged(cities.filter(c => cityIds.includes(c.id)));
      } catch (err: any) {
        this.nt.handleError(err);
      } finally {
        this.isLoading.files = false;
      }
    }
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(value => {
      value.markAsDirty();
      value.updateValueAndValidity();
    });

    if (this.form.valid) {
      const formModel = this.form.value;
      let name = this.model?.name ?? formModel.name;
      if (!name || !formModel.title || !formModel.description || !formModel.sortOrder || !formModel.startDate) {
        throw new Error('required fields');
      }
      const model: SavePromoBannerRequest = {
        id: this.model?.id,
        enabled: formModel.enabled ?? false,
        cityIds: formModel.cityIds ?? [],
        name: name,
        mainBanner: formModel.mainBanner ?? false,
        banner: formModel.banner ?? false,
        promo: formModel.promo ?? false,
        title: formModel.title,
        description: formModel.description,
        sortOrder: formModel.sortOrder,
        startDate: formModel.startDate,
        endDate: formModel.endDate ?? undefined,
      };
      model.data = {...(this.model?.data ?? {}), ...{stories: this.stories}};
      this.isSaving = true;
      try {
        const result = await this.promoBannerService.savePromo(model);
        this.form.markAsPristine();
        this.notification.success('Промо', this.tr.translate('nt.dataSaved'));
        if (this.isCreate) {
          this.router.navigate([`/promo-banners/${result.id}/edit`]);
        } else {
          this.isLoading.model = true;
          this.model = await this.promoBannerService.getPromoById(result.id);
          this.updateForm();
        }
      } catch (err: any) {
        this.nt.handleError(err);
      } finally {
        this.isLoading.model = false;
        this.isSaving = false;
      }
    } else {
      console.log('not valid form');
    }
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  handleFileUploadStatusChanged(event: NzUploadChangeParam, type: 'preview' | 'files') {
    console.log('handleFileUploadStatusChanged: ', type, event);
    if (event.type === 'success' || event.type === 'removed') {
      this.cityService.getAllCities().then(cities => {
        this.onCityChanged(cities.filter(c => (this.form.controls.cityIds.value ?? []).includes(c.id)));
      });
    }
  }

  onCityChanged(cities: CityModel | CityModel[] | undefined) {
    if (!Array.isArray(cities)) {
      return;
    }
    const filenames = this.fileList.map(file => file.name);
    const countryLocaleSet = new Set<string>();
    const countryDomainSet = new Set(cities.map(c => c.country.domain));
    countryDomainSet.forEach(domain => {
      const locales = langMap.get(domain);
      if (locales) {
        locales.forEach(locale => countryLocaleSet.add(locale));
      }
    });
    const files: { fileName: string; exist: boolean }[] = [];

    countryLocaleSet.forEach(locale => {
      files.push({fileName: `banner-480-${locale}.png`, exist: filenames.includes(`banner-480-${locale}.png`)});
      files.push({fileName: `banner-640-${locale}.png`, exist: filenames.includes(`banner-640-${locale}.png`)});
      files.push({fileName: `banner-800-${locale}.png`, exist: filenames.includes(`banner-800-${locale}.png`)});
      files.push({fileName: `banner-1030-${locale}.png`, exist: filenames.includes(`banner-1030-${locale}.png`)});
      files.push({fileName: `preview-${locale}.png`, exist: filenames.includes(`preview-${locale}.png`)});
      files.push({fileName: `${locale}.md`, exist: filenames.includes(`${locale}.md`)});
    });
    this.requiredFiles = files;
  }

  removeStory(data: PromoStory, ix: number) {
    this.stories.splice(ix, 1);
    this.form.markAsDirty();
  }

  async editStory(data: PromoStory, ix: number) {
    if (!this.model) {
      return;
    }
    const modalRef = this.modal.create({
      nzTitle: this.tr.translate('promoBanners.stories'),
      nzContent: StoreEditModal,
      nzFooter: null,
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.promo = this.model;
      modalRef.componentInstance.story = data;
    }
    const res = await firstValueFrom(modalRef.afterClose);
    if (res) {
      this.stories[ix] = res;
      this.form.markAsDirty();
    }
  }

  async addStory() {
    if (!this.model) {
      return;
    }
    const modalRef = this.modal.create({
      nzTitle: this.tr.translate('promoBanners.stories'),
      nzContent: StoreEditModal,
      nzFooter: null,
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.promo = this.model;
    }
    const res = await firstValueFrom(modalRef.afterClose);
    if (res) {
      this.stories = this.stories.concat([res]);
      this.form.markAsDirty();
    }
  }

  private updateForm() {
    if (!this.model) {
      return;
    }
    this.form.patchValue({
      name: this.model.name,
      title: this.model.title,
      description: this.model.description,
      mainBanner: this.model.mainBanner,
      banner: this.model.banner,
      promo: this.model.promo,
      enabled: this.model.enabled,
      startDate: this.model.startDate,
      endDate: this.model.endDate,
      cityIds: this.model.cityIds,
      sortOrder: this.model.sortOrder,
    });
    this.stories = this.model.data?.stories ?? [];
  }

  drop(event: any) {
    const copyArr: PromoStory[] = JSON.parse(JSON.stringify(this.stories));
    moveItemInArray(copyArr, event.previousIndex, event.currentIndex);
    this.stories = copyArr;
    this.form.markAsDirty();
  }
}
